import { FC, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  InputAdornment,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Grade as GradeIcon,
  School as SchoolIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useGradeCriteriaHook,
  useDeleteGradeCriteriaHook,
} from "../../../hooks/useAssessmentHook";
import { TGradeCriteria } from "../../../apis/assessment/assessment.interfaces";
import { logger, pages } from "../../../utils/common";
import { useClassHook, useSubjectsHook } from "../../../hooks/useAcademicHook";
import { hasAssessmentPrerequisites } from "./assessment.helpers";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ManageGradeCriteriaDialog from "./manageGradeCriteriaDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

const GradeCriteriaView: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGradeCriteriaHook();
  const {
    data: classes,
    isLoading: classesLoading,
    error: classesError,
  } = useClassHook();
  const {
    data: subjects,
    isLoading: subjectsLoading,
    error: subjectsError,
  } = useSubjectsHook();
  const deleteCriteriaMutation = useDeleteGradeCriteriaHook();
  const { control, watch } = useForm<{ search: string }>({
    defaultValues: { search: "" },
  });
  const searchQuery = watch("search");
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<TGradeCriteria | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [criteriaToDelete, setCriteriaToDelete] =
    useState<TGradeCriteria | null>(null);

  const prerequisitesMet = useMemo(
    () => hasAssessmentPrerequisites(classes, subjects),
    [classes, subjects],
  );
  const isLoadingPrerequisites = classesLoading || subjectsLoading;
  const hasPrerequisitesError = classesError || subjectsError;

  const filteredCriteria = useMemo(() => {
    if (!data) return [];

    return data.filter((criteria) => {
      const matchesSearch =
        criteria.gradeLevelName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        criteria.performanceLevel
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        criteria.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        criteria.rubrics?.some((rubric) =>
          rubric.criteria.toLowerCase().includes(searchQuery.toLowerCase()),
        );

      return matchesSearch;
    });
  }, [data, searchQuery]);

  // Sort criteria by minMarks descending
  const sortedCriteria = useMemo(() => {
    return [...filteredCriteria].sort((a, b) => b.minMarks - a.minMarks);
  }, [filteredCriteria]);

  const handleAddCriteria = () => {
    setEditingCriteria(null);
    setManageDialogOpen(true);
  };

  const handleEditCriteria = (criteria: TGradeCriteria) => {
    setEditingCriteria(criteria);
    setManageDialogOpen(true);
  };

  const handleDeleteCriteria = (criteria: TGradeCriteria) => {
    setCriteriaToDelete(criteria);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!criteriaToDelete) return;

    try {
      await deleteCriteriaMutation.mutateAsync(criteriaToDelete.id);
      logger.show_success("Grade criteria deleted successfully!");
      setDeleteDialogOpen(false);
      setCriteriaToDelete(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete grade criteria. Please try again.",
      );
    }
  };

  const handleManageDialogClose = () => {
    setManageDialogOpen(false);
    setEditingCriteria(null);
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Grade Criteria`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="grade-criteria-view"
        >
          <CircularProgress data-testid="grade-criteria-loading-spinner" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Grade Criteria`}
        />
        <Box p={3} data-testid="grade-criteria-view">
          <Alert severity="error" data-testid="grade-criteria-error">
            Failed to load grade criteria. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  if (hasPrerequisitesError) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Grade Criteria`}
        />
        <Box p={3} data-testid="grade-criteria-view">
          <Alert
            severity="error"
            data-testid="grade-criteria-prerequisites-error"
          >
            Failed to load classes or subjects data. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoadingPrerequisites) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Grade Criteria`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="grade-criteria-view"
        >
          <CircularProgress data-testid="grade-criteria-loading-spinner" />
        </Box>
      </>
    );
  }

  const showPrerequisitesEmptyState =
    !prerequisitesMet && (!data || data.length === 0);

  return (
    <>
      <DocumentTitle
        title={`${CONSTANTS.title.assessment} - Grade Criteria`}
      />

      <Grid container spacing={3} data-testid="grade-criteria-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="grade-criteria-header"
          >
            <Typography component="h2" variant="h6" fontWeight={600}>
              Grade criteria
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddCriteria}
              size="large"
              disabled={!prerequisitesMet}
              data-testid="add-grade-criteria-button"
            >
              Add Grade Criteria
            </Button>
          </Box>
        </Grid>

        {showPrerequisitesEmptyState ? (
          <Grid item xs={12}>
            <Card data-testid="grade-criteria-prerequisites-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="300px"
                  gap={2}
                >
                  <SchoolIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Alert
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ width: "100%", maxWidth: 600 }}
                    data-testid="grade-criteria-prerequisites-alert"
                  >
                    <Typography variant="h6" gutterBottom>
                      Prerequisites Required
                    </Typography>
                    <Typography variant="body2" paragraph>
                      To configure grade criteria, you must first set up classes
                      and subjects in Academics.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate(`${pages.academics.path}/subjects`)}
                      sx={{ mt: 2 }}
                      startIcon={<SchoolIcon />}
                      data-testid="navigate-to-academic-button"
                    >
                      Go to Academic Module
                    </Button>
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <>
            {/* Search */}
            <Grid item xs={12}>
              <Card data-testid="grade-criteria-filters-card">
                <CardContent>
                  <ControlledTextField
                    control={control}
                    name="search"
                    id="grade-criteria-search"
                    label="Search"
                    isGridItem={false}
                    fullWidth
                    placeholder="Search by grade level, performance level, or criteria..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ "data-testid": "grade-criteria-search-input" }}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Criteria List */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                gutterBottom
                data-testid="grade-criteria-count"
              >
                Grade Criteria ({sortedCriteria.length})
              </Typography>
            </Grid>

            {sortedCriteria.length === 0 ? (
              <Grid item xs={12}>
                <Card data-testid="grade-criteria-empty-state">
                  <CardContent>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight="200px"
                      gap={2}
                    >
                      <GradeIcon
                        sx={{ fontSize: 64, color: "text.secondary" }}
                      />
                      <Typography variant="h6" color="text.secondary">
                        {data?.length === 0
                          ? "No grade criteria configured"
                          : "No grade criteria match your search criteria"}
                      </Typography>
                      {data?.length === 0 && prerequisitesMet && (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            Get started by adding your first grade criteria.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddCriteria}
                            data-testid="add-grade-criteria-empty-button"
                          >
                            Add Grade Criteria
                          </Button>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  data-testid="grade-criteria-table-container"
                >
                  <Table data-testid="grade-criteria-table">
                    <TableHead data-testid="grade-criteria-table-head">
                      <TableRow>
                        <TableCell>Grade Level</TableCell>
                        <TableCell>Marks Range</TableCell>
                        <TableCell>Performance Level</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Rubrics</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-testid="grade-criteria-table-body">
                      {sortedCriteria.map((criteria) => (
                        <TableRow
                          key={criteria.id}
                          hover
                          data-testid={`grade-criteria-row-${criteria.id}`}
                        >
                          <TableCell>
                            <Typography variant="body1" fontWeight="medium">
                              {criteria.gradeLevelName || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {criteria.minMarks} - {criteria.maxMarks ?? "∞"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {criteria.performanceLevel ? (
                              <Chip
                                label={criteria.performanceLevel}
                                variant="outlined"
                                size="small"
                                data-testid={`performance-level-chip-${criteria.id}`}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {criteria.description || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {criteria.rubrics && criteria.rubrics.length > 0 ? (
                              <Chip
                                label={`${criteria.rubrics.length} rubric(s)`}
                                variant="outlined"
                                size="small"
                                data-testid={`rubrics-count-chip-${criteria.id}`}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                None
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleEditCriteria(criteria)}
                              color="primary"
                              data-testid={`edit-grade-criteria-${criteria.id}`}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteCriteria(criteria)}
                              color="error"
                              data-testid={`delete-grade-criteria-${criteria.id}`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <ManageGradeCriteriaDialog
        open={manageDialogOpen}
        onClose={handleManageDialogClose}
        gradeCriteria={editingCriteria}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCriteriaToDelete(null);
        }}
        data-testid="delete-grade-criteria-dialog"
      >
        <DialogTitle data-testid="delete-grade-criteria-dialog-title">
          Delete Grade Criteria
        </DialogTitle>
        <DialogContent data-testid="delete-grade-criteria-dialog-content">
          <DialogContentText>
            Are you sure you want to delete grade criteria for "
            {criteriaToDelete?.gradeLevelName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="delete-grade-criteria-dialog-actions">
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setCriteriaToDelete(null);
            }}
            disabled={deleteCriteriaMutation.isPending}
            data-testid="cancel-delete-grade-criteria-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteCriteriaMutation.isPending}
            startIcon={
              deleteCriteriaMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-grade-criteria-button"
          >
            {deleteCriteriaMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GradeCriteriaView;
